import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Trash2, Plus } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { createChangeOrder } from '@/services/changeOrderService';
import { useToast } from '@/hooks/use-toast';

export const CreateChangeOrderDialog = ({ open, onOpenChange, project, customer, onSaveSuccess }) => {
  const { toast } = useToast();
  const sigCanvas = useRef(null);

  // Form state
  const [lineItems, setLineItems] = useState([
    { description: '', price: 0, quantity: 1 }
  ]);
  const [loading, setLoading] = useState(false);

  // Calculated values
  const [changeOrderTotal, setChangeOrderTotal] = useState(0);
  const [previousTotalJobCost, setPreviousTotalJobCost] = useState(0);
  const [newProjectTotal, setNewProjectTotal] = useState(0);

  // Initialize values when dialog opens
  useEffect(() => {
    if (open && project) {
      const currentJobCost = project.total_job_cost || project.contract_price || 0;
      setPreviousTotalJobCost(currentJobCost);
      calculateTotals(lineItems, currentJobCost);
    }
  }, [open, project]);

  // Calculate totals whenever line items change
  useEffect(() => {
    calculateTotals(lineItems, previousTotalJobCost);
  }, [lineItems]);

  const calculateTotals = (items, previousCost) => {
    const total = items.reduce((sum, item) => {
      return sum + (parseFloat(item.price || 0) * parseInt(item.quantity || 0));
    }, 0);
    
    setChangeOrderTotal(total);
    setNewProjectTotal(previousCost + total);
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { description: '', price: 0, quantity: 1 }]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length > 1) {
      const newItems = lineItems.filter((_, i) => i !== index);
      setLineItems(newItems);
    }
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...lineItems];
    newItems[index][field] = value;
    setLineItems(newItems);
  };

  const clearSignature = () => {
    sigCanvas.current?.clear();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Company Header - Left side
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Reliable Landscaping and Design', 20, 20);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('License #1101544', 20, 27);
    doc.text('916-918-3990', 20, 32);
    doc.text('www.ReliableLandscapesDesign.com', 20, 37);
    
    // Document Title - Centered
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('CHANGE ORDER', pageWidth / 2, 50, { align: 'center' });
    
    // Customer Info Section - Right side
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    const rightX = 110;
    doc.text(`Customer:`, rightX, 65);
    const customerFullName = `${customer?.name || ''} ${customer?.last_name || ''}`.trim() || 'N/A';
    doc.text(customerFullName, rightX + 30, 65);
    
    doc.text(`Project Address:`, rightX, 72);
    const address = project?.jobsite_address || project?.address || 'N/A';
    const addressLines = doc.splitTextToSize(address, 65);
    doc.text(addressLines, rightX + 30, 72);
    
    doc.text(`Date:`, rightX, 79 + (addressLines.length - 1) * 5);
    doc.text(`${new Date().toLocaleDateString('en-US')}`, rightX + 30, 79 + (addressLines.length - 1) * 5);
    
    // Scope of Work Section
    const scopeStartY = 95 + (addressLines.length - 1) * 5;
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('Scope of Work', 20, scopeStartY);
    
    // Line items table
    const tableData = lineItems.map(item => [
      item.description,
      item.quantity.toString(),
      formatCurrency(item.price),
      formatCurrency(item.price * item.quantity)
    ]);
    
    autoTable(doc, {
      startY: scopeStartY + 5,
      head: [['Description', 'Quantity', 'Unit Price', 'Total']],
      body: tableData,
      theme: 'grid',
      headStyles: { 
        fillColor: [41, 128, 185],
        fontSize: 11,
        fontStyle: 'bold',
        halign: 'left'
      },
      bodyStyles: {
        fontSize: 10
      },
      columnStyles: {
        0: { cellWidth: 90 },
        1: { cellWidth: 25, halign: 'center' },
        2: { cellWidth: 35, halign: 'right' },
        3: { cellWidth: 35, halign: 'right' }
      },
      margin: { left: 20, right: 20 }
    });
    
    const finalY = (doc.lastAutoTable?.finalY || doc.previousAutoTable?.finalY || 150) + 15;
    
    // Cost Summary Section
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('Cost Summary', 20, finalY);
    
    // Cost summary table
    const costData = [
      ['Change Order Total', formatCurrency(changeOrderTotal)],
      ['Previous Job Total', formatCurrency(previousTotalJobCost)],
      ['New Project Total', formatCurrency(newProjectTotal)]
    ];
    
    autoTable(doc, {
      startY: finalY + 5,
      body: costData,
      theme: 'plain',
      bodyStyles: {
        fontSize: 11
      },
      columnStyles: {
        0: { cellWidth: 80, fontStyle: 'bold' },
        1: { cellWidth: 50, halign: 'right', fontStyle: 'bold' }
      },
      margin: { left: 20 }
    });
    
    const costSummaryEndY = (doc.lastAutoTable?.finalY || finalY + 35) + 15;
    
    // Terms & Conditions Section
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('Terms & Conditions', 20, costSummaryEndY);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    let termsY = costSummaryEndY + 7;
    const terms = [
      'Scope & Authorization: This Change Order modifies the original contract. Work will commence upon signed approval.',
      'Pricing & Payments: All prices are final. Payment terms remain as per the original contract.',
      'Delays & Scheduling: Changes may affect the project timeline. New completion dates will be communicated.'
    ];
    
    terms.forEach(term => {
      const lines = doc.splitTextToSize(term, pageWidth - 40);
      doc.text(lines, 20, termsY);
      termsY += lines.length * 5 + 3;
    });
    
    // Check if we need a new page for signature
    if (termsY > 240) {
      doc.addPage();
      termsY = 20;
    }
    
    // Warranty section
    termsY += 5;
    doc.setFont('helvetica', 'bold');
    doc.text('Warranty', 20, termsY);
    doc.setFont('helvetica', 'normal');
    termsY += 7;
    const warrantyText = doc.splitTextToSize(
      'All work performed under this Change Order is warranted for the same period as the original contract.',
      pageWidth - 40
    );
    doc.text(warrantyText, 20, termsY);
    termsY += warrantyText.length * 5 + 8;
    
    // Binding Agreement
    doc.setFont('helvetica', 'bold');
    doc.text('Binding Agreement', 20, termsY);
    doc.setFont('helvetica', 'normal');
    termsY += 7;
    const agreementText = doc.splitTextToSize(
      'By signing below, both parties agree to the terms outlined in this Change Order and acknowledge that it becomes part of the original contract.',
      pageWidth - 40
    );
    doc.text(agreementText, 20, termsY);
    termsY += agreementText.length * 5 + 10;
    
    // Check if we need a new page for signatures
    if (termsY > 220) {
      doc.addPage();
      termsY = 30;
    }
    
    // Customer Signature Section
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Customer Signature:', 20, termsY);
    
    // Add signature image if present, otherwise leave blank space
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const signatureImage = sigCanvas.current.toDataURL();
      doc.addImage(signatureImage, 'PNG', 20, termsY + 5, 60, 20);
      termsY += 25;
    } else {
      // Leave blank space for signature
      termsY += 25;
    }
    
    // Signature line and date for customer
    doc.setFont('helvetica', 'normal');
    doc.line(20, termsY, 90, termsY);
    doc.setFontSize(9);
    doc.text('Signature', 20, termsY + 5);
    doc.text(`Date: ${new Date().toLocaleDateString('en-US')}`, 20, termsY + 10);
    
    // Contractor Signature Section
    const contractorY = termsY - 25;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Contractor Signature:', 110, contractorY);
    
    // Blank space for contractor signature
    doc.setFont('helvetica', 'normal');
    doc.line(110, termsY, 180, termsY);
    doc.setFontSize(9);
    doc.text('Signature', 110, termsY + 5);
    doc.text('Date: ________________', 110, termsY + 10);
    
    return doc;
  };

  const handleDownload = () => {
    try {
      console.log('🔧 Starting PDF generation...');
      const doc = generatePDF();
      console.log('✅ PDF generated successfully');
      
      const fileName = `Change_Order_${project?.name || 'Project'}_${new Date().toISOString().split('T')[0]}.pdf`;
      console.log('🔧 Attempting to save PDF as:', fileName);
      
      // Try the standard save method
      doc.save(fileName);
      console.log('✅ doc.save() called successfully');
      
      toast({
        title: 'Success',
        description: 'Change order PDF downloaded successfully.',
      });
    } catch (error) {
      console.error('❌ PDF generation error:', error);
      console.error('❌ Error details:', error.message, error.stack);
      toast({
        title: 'Error',
        description: `Failed to generate PDF: ${error.message}`,
        variant: 'destructive',
      });
    }
  };

  const handleSave = async () => {
    // Validation
    if (lineItems.some(item => !item.description.trim())) {
      toast({
        title: 'Validation Error',
        description: 'All line items must have a description.',
        variant: 'destructive',
      });
      return;
    }

    if (changeOrderTotal === 0) {
      toast({
        title: 'Validation Error',
        description: 'Change order total cannot be zero.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      // Get signature as base64
      let signatureData = null;
      if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
        signatureData = sigCanvas.current.toDataURL();
      }

      // Prepare data - MongoDB expects string IDs
      const changeOrderData = {
        project_id: project.id || project._id,
        customer_id: customer.id || customer._id,
        change_order_data: lineItems.map(item => ({
          description: item.description,
          price: parseFloat(item.price),
          quantity: parseInt(item.quantity)
        })),
        total: changeOrderTotal,
        previous_total_job_cost: previousTotalJobCost,
        new_project_total: newProjectTotal,
        status: signatureData ? 'Signed' : 'Unsigned',
        signature: signatureData,
      };

      // Save to backend
      await createChangeOrder(changeOrderData);

      toast({
        title: 'Success',
        description: 'Change order saved successfully.',
      });

      // Reset form
      setLineItems([{ description: '', price: 0, quantity: 1 }]);
      clearSignature();
      
      // Close dialog and trigger refresh
      onOpenChange(false);
      if (onSaveSuccess) {
        onSaveSuccess();
      }
    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save change order.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Change Order</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Customer Information (Read-only) */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Customer Name</Label>
              <Input value={customer?.name || ''} readOnly className="bg-gray-50" />
            </div>
            <div>
              <Label>Project Address</Label>
              <Input value={project?.jobsite_address || project?.address || ''} readOnly className="bg-gray-50" />
            </div>
          </div>

          {/* Scope of Work */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <Label className="text-lg font-semibold">Scope of Work</Label>
              <Button variant="outline" size="sm" onClick={addLineItem}>
                <Plus className="w-4 h-4 mr-2" />
                Add Item
              </Button>
            </div>

            <div className="space-y-3">
              {lineItems.map((item, index) => (
                <Card key={index}>
                  <CardContent className="pt-4">
                    <div className="grid grid-cols-12 gap-3 items-start">
                      {/* Description */}
                      <div className="col-span-6">
                        <Label>Description</Label>
                        <Textarea
                          value={item.description}
                          onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                          placeholder="Enter work description"
                          rows={2}
                        />
                      </div>

                      {/* Quantity */}
                      <div className="col-span-2">
                        <Label>Quantity</Label>
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateLineItem(index, 'quantity', parseInt(e.target.value) || 0)}
                        />
                      </div>

                      {/* Price */}
                      <div className="col-span-2">
                        <Label>Unit Price</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.price}
                          onChange={(e) => updateLineItem(index, 'price', parseFloat(e.target.value) || 0)}
                          placeholder="0.00"
                        />
                      </div>

                      {/* Total */}
                      <div className="col-span-2 flex items-end gap-2">
                        <div className="flex-1">
                          <Label>Line Total</Label>
                          <Input
                            value={formatCurrency(item.price * item.quantity)}
                            readOnly
                            className="bg-gray-50"
                          />
                        </div>
                        {lineItems.length > 1 && (
                          <Button
                            variant="destructive"
                            size="icon"
                            onClick={() => removeLineItem(index)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Cost Summary */}
          <div className="bg-gray-50 p-4 rounded-lg space-y-3">
            <div className="flex justify-between text-lg">
              <span className="font-semibold">Change Order Total:</span>
              <span className="font-bold text-blue-600">{formatCurrency(changeOrderTotal)}</span>
            </div>
            <div className="flex justify-between">
              <span>Previous Total Job Cost:</span>
              <span>{formatCurrency(previousTotalJobCost)}</span>
            </div>
            <div className="border-t pt-2 flex justify-between text-xl">
              <span className="font-bold">New Project Total:</span>
              <span className="font-bold text-green-600">{formatCurrency(newProjectTotal)}</span>
            </div>
          </div>

          {/* Signature Section */}
          <div>
            <Label className="text-lg font-semibold">Customer Signature</Label>
            <p className="text-sm text-gray-500 mb-2">
              If signed, this change order will be immediately added to the project total.
            </p>
            <div className="border-2 border-gray-300 rounded-lg">
              <SignatureCanvas
                ref={sigCanvas}
                penColor="black"
                canvasProps={{
                  width: 750,
                  height: 150,
                  className: 'signature-canvas w-full'
                }}
              />
            </div>
            <Button variant="link" size="sm" onClick={clearSignature} className="mt-1">
              Clear Signature
            </Button>
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={handleDownload} disabled={loading}>
            Download PDF
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : 'Save to Job'}
          </Button>
          <DialogClose asChild>
            <Button variant="ghost">Cancel</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
